/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Button,
  Switch,
  useTheme,
  alpha
} from '@mui/material';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { getTankDetails, updateTank } from 'src/redux/tankSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import useAuth from 'src/context/AuthContext';
import {
  hasAdminLikeAccess,
  getFormattedDate
} from 'src/utils/getInitials';
import NewTank from './NewTank';

/* -------------------- Helpers -------------------- */

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const isISODate = (value) => {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return typeof value === 'string' && isoRegex.test(value);
};

const formatValue = (key, value) => {
  if (value === null || value === undefined) return '--';

  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';

  if (isISODate(value)) {
    return getFormattedDate(value);
  }

  return value;
};

const getDispenserName = (tank) => tank?.dispenser_name
  || tank?.dispenser?.station_name
  || tank?.dispenser?.name
  || tank?.dispenser?.dispenser_name
  || '--';

const shouldRenderField = (key, value) => {
  if (typeof value === 'object' && value !== null) return false;

  const excludedFields = ['site', 'current_balance', 'dispenser_name', 'dispenser'];
  if (excludedFields.includes(key)) return false;

  return true;
};

/* -------------------- Component -------------------- */

function TankDetails() {
  const theme = useTheme();
  const { tankId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const { loading, tankDetails } = useSelector((state) => state.tanks);

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getTankDetails(tankId));
  }, [dispatch, tankId]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const values = {
        ...tankDetails,
        automation: !tankDetails.automation
      };

      await dispatch(updateTank(values)).unwrap();

      dispatch(
        setSuccessInfo({ successMsg: 'Tank status updated successfully!' })
      );
    } catch (error) {
      dispatch(
        setErrorInfo({
          errorMsg:
            error?.response?.data?.error_message
            || error?.message
            || 'Failed to update tank status'
        })
      );
    }

    setIsLoading(false);
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewTank
        open={openModal}
        initialValues={tankDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)',
        }}
      >
        {/* HEADER */}
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                TANK DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage tank configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.tanks)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Back
            </Button>
          )}
        />

        {/* CONTENT */}
        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} key="dispenser_name">
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.5
                  }}
                >
                  Dispenser Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {getDispenserName(tankDetails)}
                </Typography>
              </Box>
            </Grid>

            {Object.entries(tankDetails || {})
              .filter(([key, value]) => shouldRenderField(key, value))
              .map(([key, value]) => {
                if (key === 'automation') {
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(
                              theme.palette.primary.main,
                              0.02
                            )
                          }
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            AUTOMATION
                          </Typography>
                          <Typography
                            fontWeight={600}
                            color={value ? 'success.main' : 'error.main'}
                          >
                            {value ? 'Enabled' : 'Disabled'}
                          </Typography>
                        </Box>

                        <Switch
                          checked={value}
                          onChange={() => hasAdminAccess && setShowConfirmModal(true)}
                          disabled={!hasAdminAccess}
                          color="success"
                        />
                      </Box>
                    </Grid>
                  );
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(
                            theme.palette.primary.main,
                            0.02
                          )
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 0.5
                        }}
                      >
                        {formatLabel(key)}
                      </Typography>

                      <Typography variant="body1" fontWeight={600}>
                        {formatValue(key, value)}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </CardContent>

        {/* ACTION */}
        {hasAdminAccess && (
          <>
            <Divider />
            <CardActions sx={{
              p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
            }}
            >
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Edit Tank Details
              </Button>
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        info={
          tankDetails?.automation
            ? CONFIRMATION_MSG.disableTank
            : CONFIRMATION_MSG.enableTank
        }
        open={showConfirmModal}
        disable={tankDetails?.automation}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default TankDetails;
